import { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Upload, FileText, Shield, ShieldCheck,
  Clock, AlertTriangle, CheckCircle, XCircle, ChevronRight,
  CreditCard, Car, Home as HomeIcon, FileCheck,
} from 'lucide-react';
import { api } from '../lib/api';

// ============================================================================
// TYPES
// ============================================================================

type IdentityStatus = 'UNVERIFIED' | 'PENDING' | 'VERIFIED' | 'REJECTED';
type DocumentType = 'passport' | 'id_card' | 'drivers_license' | 'residence_permit';

interface VerificationState {
  status: IdentityStatus;
  message: string;
  documentType?: string | null;
  verifiedAt?: string | null;
  submittedAt?: string | null;
  reviewNote?: string | null;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const DOCUMENT_OPTIONS: { type: DocumentType; label: string; icon: typeof FileText; description: string }[] = [
  { type: 'passport', label: 'Passport', icon: FileText, description: 'International travel document' },
  { type: 'id_card', label: 'National ID Card', icon: CreditCard, description: 'Government-issued identity card' },
  { type: 'drivers_license', label: "Driver's License", icon: Car, description: 'Valid driving permit with photo' },
  { type: 'residence_permit', label: 'Residence Permit', icon: HomeIcon, description: 'Proof of legal residence' },
];

const STATUS_CONFIG: Record<IdentityStatus, { color: string; bgColor: string; borderColor: string; icon: typeof Shield; label: string }> = {
  UNVERIFIED: { color: 'text-hig-text-tertiary', bgColor: 'bg-hig-bg-tertiary', borderColor: 'border-hig-border', icon: Shield, label: 'Not Verified' },
  PENDING: { color: 'text-amber-400', bgColor: 'bg-amber-500/10', borderColor: 'border-amber-500/20', icon: Clock, label: 'Under Review' },
  VERIFIED: { color: 'text-emerald-400', bgColor: 'bg-emerald-500/10', borderColor: 'border-emerald-500/20', icon: ShieldCheck, label: 'Verified' },
  REJECTED: { color: 'text-red-400', bgColor: 'bg-red-500/10', borderColor: 'border-red-500/20', icon: XCircle, label: 'Rejected' },
};

// ============================================================================
// COMPONENT
// ============================================================================

interface IdentityVerificationViewProps {
  onBack: () => void;
}

export default function IdentityVerificationView({ onBack }: IdentityVerificationViewProps) {
  const [verificationState, setVerificationState] = useState<VerificationState | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Multi-step form state
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedDocType, setSelectedDocType] = useState<DocumentType | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // ── Fetch current status ──────────────────────────────────────────
  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get<VerificationState>('/identity-verification/status');
      if (res.data) {
        setVerificationState(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch verification status:', err);
      setVerificationState({
        status: 'UNVERIFIED',
        message: 'Unable to load verification status. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // ── Submit verification ───────────────────────────────────────────
  const handleSubmit = async () => {
    if (!selectedDocType || !selectedFile) return;
    setSubmitting(true);

    try {
      // In production, the file would be uploaded to cloud storage first.
      // For now, we create a local object URL as a placeholder reference.
      const documentUrl = `uploads/identity/${Date.now()}-${selectedFile.name}`;

      // Upload the file via the upload route
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('type', 'identity-document');

      try {
        await api.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } catch {
        // Upload endpoint may not handle identity docs yet — proceed with placeholder URL
      }

      const res = await api.post<VerificationState>('/identity-verification/submit', {
        documentType: selectedDocType,
        documentUrl,
      });

      if (res.data?.status) {
        setVerificationState(res.data);
      }
    } catch (err) {
      console.error('Failed to submit verification:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Reset after rejection ─────────────────────────────────────────
  const handleReset = async () => {
    setResetting(true);
    try {
      const res = await api.post<VerificationState>('/identity-verification/reset');
      if (res.data?.status) {
        setVerificationState(res.data);
        setStep(1);
        setSelectedDocType(null);
        setSelectedFile(null);
        setAcceptedTerms(false);
      }
    } catch (err) {
      console.error('Failed to reset verification:', err);
    } finally {
      setResetting(false);
    }
  };

  // ── File handlers ─────────────────────────────────────────────────
  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && isValidFile(file)) {
      setSelectedFile(file);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && isValidFile(file)) {
      setSelectedFile(file);
    }
  };

  const isValidFile = (file: File): boolean => {
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    const maxSize = 10 * 1024 * 1024; // 10MB
    return validTypes.includes(file.type) && file.size <= maxSize;
  };

  // ── Loading state ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="max-w-2xl mx-auto flex items-center justify-center py-24">
        <div className="w-6 h-6 border-2 border-hig-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const status = verificationState?.status || 'UNVERIFIED';
  const config = STATUS_CONFIG[status];
  const StatusIcon = config.icon;

  // ════════════════════════════════════════════════════════════════════
  // STATUS BANNER (shown for PENDING, VERIFIED, REJECTED)
  // ════════════════════════════════════════════════════════════════════
  if (status !== 'UNVERIFIED') {
    return (
      <div className="max-w-2xl mx-auto space-y-6 pb-12">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 text-hig-text-tertiary hover:text-hig-text-primary hover:bg-hig-bg-tertiary rounded-xl transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-hig-text-primary">Identity Verification</h1>
            <p className="text-sm text-hig-text-secondary">Document-based KYC verification</p>
          </div>
        </div>

        {/* Status Card */}
        <div className={`card p-8 text-center border-2 ${config.borderColor}`}>
          <div className={`w-16 h-16 rounded-2xl ${config.bgColor} flex items-center justify-center mx-auto mb-4`}>
            <StatusIcon size={32} className={config.color} />
          </div>
          <h2 className={`text-xl font-bold ${config.color} mb-2`}>{config.label}</h2>
          <p className="text-sm text-hig-text-secondary max-w-md mx-auto leading-relaxed">
            {verificationState?.message}
          </p>

          {/* Meta info */}
          <div className="mt-6 space-y-2">
            {verificationState?.documentType && (
              <p className="text-xs text-hig-text-tertiary">
                Document: <span className="text-hig-text-secondary capitalize">{verificationState.documentType.replace('_', ' ')}</span>
              </p>
            )}
            {verificationState?.submittedAt && (
              <p className="text-xs text-hig-text-tertiary">
                Submitted: <span className="text-hig-text-secondary">{new Date(verificationState.submittedAt).toLocaleDateString('en-US', { dateStyle: 'medium' })}</span>
              </p>
            )}
            {verificationState?.verifiedAt && (
              <p className="text-xs text-hig-text-tertiary">
                Verified: <span className="text-emerald-400">{new Date(verificationState.verifiedAt).toLocaleDateString('en-US', { dateStyle: 'medium' })}</span>
              </p>
            )}
          </div>

          {/* Rejection details */}
          {status === 'REJECTED' && verificationState?.reviewNote && (
            <div className="mt-6 p-4 bg-red-500/5 border border-red-500/10 rounded-xl text-left">
              <div className="flex items-start gap-2">
                <AlertTriangle size={16} className="text-red-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-400 mb-1">Rejection Reason</p>
                  <p className="text-sm text-hig-text-secondary">{verificationState.reviewNote}</p>
                </div>
              </div>
            </div>
          )}

          {/* Resubmit button (only for REJECTED) */}
          {status === 'REJECTED' && (
            <button
              onClick={handleReset}
              disabled={resetting}
              className="mt-6 px-6 py-3 bg-hig-accent text-white rounded-xl text-sm font-semibold hover:bg-hig-accent/90 disabled:opacity-50 transition-colors"
            >
              {resetting ? 'Resetting...' : 'Submit New Document'}
            </button>
          )}

          {/* Verified badge indicator */}
          {status === 'VERIFIED' && (
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full">
              <ShieldCheck size={16} className="text-emerald-400" />
              <span className="text-sm font-medium text-emerald-400">Identity Verified</span>
            </div>
          )}

          {/* Pending animation */}
          {status === 'PENDING' && (
            <div className="mt-6 flex items-center justify-center gap-3">
              <div className="flex gap-1">
                <div className="w-2 h-2 rounded-full bg-amber-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 rounded-full bg-amber-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 rounded-full bg-amber-400 animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
              <span className="text-xs text-hig-text-tertiary">Review in progress</span>
            </div>
          )}
        </div>

        {/* Trust info */}
        <div className="card p-5">
          <div className="flex items-start gap-3">
            <Shield size={18} className="text-hig-accent mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-hig-text-primary mb-1">Why Verify?</p>
              <p className="text-xs text-hig-text-secondary leading-relaxed">
                Identity verification unlocks enhanced trust across the platform: verified badges on your profile, 
                higher autonomy levels for your replicas, guardian eligibility, and priority placement in the marketplace.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════
  // MULTI-STEP FORM (for UNVERIFIED users)
  // ════════════════════════════════════════════════════════════════════

  return (
    <div className="max-w-2xl mx-auto space-y-6 pb-12">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="p-2 text-hig-text-tertiary hover:text-hig-text-primary hover:bg-hig-bg-tertiary rounded-xl transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-hig-text-primary">Identity Verification</h1>
          <p className="text-sm text-hig-text-secondary">Verify your identity to unlock full platform trust</p>
        </div>
      </div>

      {/* Progress indicator */}
      <div className="flex items-center gap-2">
        {[1, 2, 3].map((s) => (
          <div key={s} className="flex items-center gap-2 flex-1">
            <div className={`
              w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300
              ${step >= s
                ? 'bg-hig-accent text-white shadow-lg shadow-hig-accent/20'
                : 'bg-hig-bg-tertiary text-hig-text-tertiary'
              }
            `}>
              {step > s ? <CheckCircle size={16} /> : s}
            </div>
            {s < 3 && (
              <div className={`flex-1 h-0.5 rounded-full transition-all duration-300 ${step > s ? 'bg-hig-accent' : 'bg-hig-bg-tertiary'}`} />
            )}
          </div>
        ))}
      </div>

      {/* Step labels */}
      <div className="flex justify-between text-[10px] uppercase tracking-wider font-bold px-1">
        <span className={step >= 1 ? 'text-hig-accent' : 'text-hig-text-tertiary'}>Document Type</span>
        <span className={step >= 2 ? 'text-hig-accent' : 'text-hig-text-tertiary'}>Upload</span>
        <span className={step >= 3 ? 'text-hig-accent' : 'text-hig-text-tertiary'}>Confirm</span>
      </div>

      {/* ─── Step 1: Document Type Selection ───────────────────────── */}
      {step === 1 && (
        <div className="card p-6 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-hig-text-primary mb-1">Select Document Type</h2>
            <p className="text-sm text-hig-text-secondary">
              Choose a government-issued document to verify your identity
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {DOCUMENT_OPTIONS.map((doc) => {
              const Icon = doc.icon;
              const isSelected = selectedDocType === doc.type;
              return (
                <button
                  key={doc.type}
                  onClick={() => setSelectedDocType(doc.type)}
                  className={`
                    relative p-4 rounded-xl border-2 text-left transition-all duration-200 group
                    ${isSelected
                      ? 'border-hig-accent bg-hig-accent/5 shadow-lg shadow-hig-accent/10'
                      : 'border-hig-border hover:border-hig-accent/30 hover:bg-hig-bg-tertiary/50'
                    }
                  `}
                >
                  <div className="flex items-start gap-3">
                    <div className={`
                      w-10 h-10 rounded-xl flex items-center justify-center transition-colors
                      ${isSelected ? 'bg-hig-accent text-white' : 'bg-hig-bg-tertiary text-hig-text-secondary group-hover:text-hig-accent'}
                    `}>
                      <Icon size={20} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-hig-text-primary">{doc.label}</p>
                      <p className="text-xs text-hig-text-tertiary mt-0.5">{doc.description}</p>
                    </div>
                  </div>
                  {isSelected && (
                    <div className="absolute top-3 right-3">
                      <CheckCircle size={18} className="text-hig-accent" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex justify-end pt-2">
            <button
              onClick={() => setStep(2)}
              disabled={!selectedDocType}
              className="flex items-center gap-2 px-6 py-2.5 bg-hig-accent text-white rounded-xl text-sm font-semibold hover:bg-hig-accent/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Continue
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* ─── Step 2: File Upload ────────────────────────────────────── */}
      {step === 2 && (
        <div className="card p-6 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-hig-text-primary mb-1">Upload Your Document</h2>
            <p className="text-sm text-hig-text-secondary">
              Upload a clear photo or scan of your {selectedDocType?.replace('_', ' ')}
            </p>
          </div>

          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleFileDrop}
            className={`
              relative border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-200
              ${dragOver
                ? 'border-hig-accent bg-hig-accent/5 scale-[1.01]'
                : selectedFile
                  ? 'border-emerald-500/30 bg-emerald-500/5'
                  : 'border-hig-border hover:border-hig-accent/30 hover:bg-hig-bg-tertiary/30'
              }
            `}
          >
            {selectedFile ? (
              <div className="space-y-3">
                <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto">
                  <FileCheck size={28} className="text-emerald-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-hig-text-primary">{selectedFile.name}</p>
                  <p className="text-xs text-hig-text-tertiary mt-0.5">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB · {selectedFile.type.split('/')[1]?.toUpperCase()}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedFile(null)}
                  className="text-xs text-hig-accent hover:underline"
                >
                  Remove and choose another
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="w-14 h-14 rounded-2xl bg-hig-bg-tertiary flex items-center justify-center mx-auto">
                  <Upload size={28} className="text-hig-text-tertiary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-hig-text-primary">
                    Drag & drop your document here
                  </p>
                  <p className="text-xs text-hig-text-tertiary mt-1">
                    or click to browse · JPEG, PNG, WebP, PDF up to 10 MB
                  </p>
                </div>
                <label className="inline-flex items-center gap-2 px-4 py-2 bg-hig-bg-tertiary text-hig-text-primary rounded-lg text-sm font-medium cursor-pointer hover:bg-hig-bg-elevated transition-colors">
                  <Upload size={14} />
                  Browse Files
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    onChange={handleFileSelect}
                    className="sr-only"
                  />
                </label>
              </div>
            )}
          </div>

          {/* Tips */}
          <div className="p-4 bg-hig-bg-tertiary/50 rounded-xl">
            <p className="text-xs font-semibold text-hig-text-secondary mb-2">Tips for a successful verification:</p>
            <ul className="text-xs text-hig-text-tertiary space-y-1 list-disc list-inside">
              <li>Ensure all four corners of the document are visible</li>
              <li>The text and photo must be clearly readable</li>
              <li>Avoid glare or shadows on the document</li>
              <li>The document must not be expired</li>
            </ul>
          </div>

          <div className="flex justify-between pt-2">
            <button
              onClick={() => setStep(1)}
              className="px-4 py-2.5 text-sm text-hig-text-secondary hover:text-hig-text-primary transition-colors"
            >
              Back
            </button>
            <button
              onClick={() => setStep(3)}
              disabled={!selectedFile}
              className="flex items-center gap-2 px-6 py-2.5 bg-hig-accent text-white rounded-xl text-sm font-semibold hover:bg-hig-accent/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Continue
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* ─── Step 3: Confirmation ───────────────────────────────────── */}
      {step === 3 && (
        <div className="card p-6 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-hig-text-primary mb-1">Review & Confirm</h2>
            <p className="text-sm text-hig-text-secondary">
              Please review your submission before confirming
            </p>
          </div>

          {/* Summary */}
          <div className="space-y-3">
            <div className="flex items-center justify-between p-4 bg-hig-bg-tertiary/50 rounded-xl">
              <div className="flex items-center gap-3">
                <FileText size={18} className="text-hig-accent" />
                <div>
                  <p className="text-xs text-hig-text-tertiary uppercase tracking-wider font-bold">Document Type</p>
                  <p className="text-sm font-medium text-hig-text-primary capitalize mt-0.5">
                    {selectedDocType?.replace('_', ' ')}
                  </p>
                </div>
              </div>
              <button onClick={() => setStep(1)} className="text-xs text-hig-accent hover:underline">
                Change
              </button>
            </div>

            <div className="flex items-center justify-between p-4 bg-hig-bg-tertiary/50 rounded-xl">
              <div className="flex items-center gap-3">
                <Upload size={18} className="text-hig-accent" />
                <div>
                  <p className="text-xs text-hig-text-tertiary uppercase tracking-wider font-bold">File</p>
                  <p className="text-sm font-medium text-hig-text-primary mt-0.5">
                    {selectedFile?.name}
                  </p>
                  <p className="text-xs text-hig-text-tertiary">
                    {selectedFile ? `${(selectedFile.size / 1024 / 1024).toFixed(2)} MB` : ''}
                  </p>
                </div>
              </div>
              <button onClick={() => setStep(2)} className="text-xs text-hig-accent hover:underline">
                Change
              </button>
            </div>
          </div>

          {/* Terms */}
          <label className="flex items-start gap-3 cursor-pointer group p-4 bg-hig-bg-tertiary/30 rounded-xl border border-hig-border hover:border-hig-accent/20 transition-colors">
            <div className="relative mt-0.5">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-5 h-5 border-2 border-hig-border rounded bg-hig-bg-tertiary peer-checked:bg-hig-accent peer-checked:border-hig-accent transition-colors flex items-center justify-center">
                {acceptedTerms && (
                  <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
            </div>
            <span className="text-sm text-hig-text-secondary leading-relaxed">
              I confirm that the document I&apos;m submitting is genuine, belongs to me, and I authorize HumanReplica® to process it for identity verification purposes in accordance with the{' '}
              <span className="text-hig-accent">Privacy Policy</span>.
            </span>
          </label>

          {/* Actions */}
          <div className="flex justify-between pt-2">
            <button
              onClick={() => setStep(2)}
              className="px-4 py-2.5 text-sm text-hig-text-secondary hover:text-hig-text-primary transition-colors"
            >
              Back
            </button>
            <button
              onClick={handleSubmit}
              disabled={!acceptedTerms || submitting}
              className="flex items-center gap-2 px-8 py-3 bg-hig-accent text-white rounded-xl text-sm font-bold hover:bg-hig-accent/90 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-lg shadow-hig-accent/20 hover:shadow-hig-accent/30"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <ShieldCheck size={16} />
                  Submit for Verification
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Trust info */}
      <div className="card p-5">
        <div className="flex items-start gap-3">
          <Shield size={18} className="text-hig-accent mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-hig-text-primary mb-1">Your Data is Safe</p>
            <p className="text-xs text-hig-text-secondary leading-relaxed">
              Documents are encrypted at rest and in transit. They are only accessed during the verification process 
              and are securely deleted after review. We never share your identity documents with third parties.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
